import { useCallback, useMemo } from 'react'
import Button from 'core/components/buttons/Button'
import Modal from 'core/components/dialogs/Modal'
import Icon from 'core/components/icons/Icon'
import { useSettings } from 'core/hooks/settings/useSettings'
import { FundingModalFormValues } from 'core/widgets/funding-modal/FundingModal'
import useIndexedPayPeriods from 'core/widgets/funding-modal/useIndexedPayPeriods'
import numeral from 'numeral'
import { useFormContext } from 'react-hook-form'
import {
  getPayPeriodsTotal,
  getSelectedPayPeriodsFromIds
} from 'utils/Functions'

const FundingSuccessModal = () => {
  const {
    set,
    data: {
      fundingModal: { show, showSuccessModal }
    }
  } = useSettings()
  const { watch, reset } = useFormContext<FundingModalFormValues>()
  const { indexedPayPeriods, allPayPeriodsTotal } = useIndexedPayPeriods()
  const [
    customAmount,
    amountType,
    selectedPayPeriodIds,
    fundingMethod,
    autoPay
  ] = watch([
    'customAmount',
    'amountType',
    'selected_pay_periods',
    'funding_method',
    'auto_pay'
  ])

  const selectedPayPeriods = useMemo(
    () => getSelectedPayPeriodsFromIds(indexedPayPeriods, selectedPayPeriodIds),
    [selectedPayPeriodIds, indexedPayPeriods]
  )

  const finalAmount = useMemo(() => {
    if (amountType === 'other-amount') return customAmount

    let _finalAmount = 0
    if (amountType === 'selected-pay-periods') {
      _finalAmount = getPayPeriodsTotal(selectedPayPeriods)
    }
    if (amountType === 'all-pay-periods') {
      _finalAmount = allPayPeriodsTotal
    }
    return numeral(_finalAmount).format('$0,0.00')
  }, [selectedPayPeriods, allPayPeriodsTotal, customAmount, amountType])

  const { title, body } = useMemo(() => {
    const content = {
      title: '',
      body: <></>
    }
    if (fundingMethod !== 'ach-debit') return content
    if (amountType === 'other-amount') {
      content.title = `You've initiated ${finalAmount} to fund your Wallet.`
      content.body = (
        <>
          Funding expected to arrive in 4-5 business days. Once funds are
          available in your Wallet, please return to pay reports.
        </>
      )
    }
    if (amountType !== 'other-amount') {
      content.title = `You've initiated ${finalAmount} to fund ${amountType === 'all-pay-periods' ? 'all' : selectedPayPeriodIds.length} pay period(s).`

      if (autoPay === true) {
        content.body = (
          <>
            <div className="mb-2">
              Funding expected to arrive in 4-5 business days. Once funds are
              available, reports will be automatically paid.
            </div>
            <div>
              Go to Payment Initiated list under Reimbursements to cancel.
            </div>
          </>
        )
      }
      if (autoPay === false) {
        content.body = (
          <>
            <div className="mb-2">
              Funding expected to arrive in 4-5 business days. Reports in pay
              period will <span className="font-bold">not</span> be auto-paid.
            </div>
            <div>
              Please return once Wallet funds are available to complete payment.
            </div>
          </>
        )
      }
    }
    return content
  }, [amountType, fundingMethod, autoPay, selectedPayPeriodIds, finalAmount])

  const onClick = useCallback(() => {
    reset()
    set('fundingModal', {
      show: false,
      showSuccessModal: false
    })
  }, [reset, set])

  return (
    <Modal show={!show && showSuccessModal} onClose={() => reset()}>
      <div className="flex flex-col items-center">
        <Icon
          name="check"
          color="text-white"
          size={40}
          className="rounded-full bg-primary p-2 mb-5"
        />
        <h2 className="subtitle-1 mb-4 text-center">{title}</h2>
        <p className="text-dark mb-5 text-center">{body}</p>
        <Button
          type="submit"
          variant="primary"
          className="w-56"
          onClick={onClick}
        >
          Ok
        </Button>
      </div>
    </Modal>
  )
}

export default FundingSuccessModal
